import React, { useState, useRef, useEffect } from 'react';
import { loadUserDataRaw, updateUserDataArray } from '../db/company';

// Same env-aware URL pattern as company.js
const API_BASE = process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_ENV === 'test' || process.env.REACT_APP_ENV === 'dev')
            ? 'https://forecastingprodservice.azurewebsites.net/'
            : 'https://forecastingprodservice.azurewebsites.net/'
        : 'https://forecastingtestservice.azurewebsites.net/');

export const AI_PROMPT_STORAGE_KEY = 'aiInsightsSystemPrompt';
export const AI_USER_PROMPT_STORAGE_KEY = 'aiInsightsLastUserPrompt';

export const DEFAULT_AI_SYSTEM_PROMPT = 'You are an aggressive, no-nonsense construction project controls analyst. Give a brief summary in 2-3 bullet points max. Be specific with dollar amounts. No headers or sections — just concise bullets. Do NOT state simple facts about margins — explain WHY the margin is where it is by examining the underlying discipline-level data, cost trends, and team behavior. Be blunt and direct. Never blame the PM individually — always refer to "the project team" or "the team". DELTA INTERPRETATION: Delta = EAC minus Cost Budget. A NEGATIVE delta means EAC is LESS than budget — the project is coming in UNDER budget, which is a GOOD thing. A POSITIVE delta means EAC EXCEEDS budget — that is a problem. Do not confuse these. CONTINGENCY: Do NOT mention contingency at all unless the project is more than 85% physically complete. If it is above 85% and the team still has most of their contingency sitting as ETC, they are sandbagging — call it out. Only flag contingency as a real concern if actual spend is trending to exceed it. BUDGET DISCIPLINE: Aggressively question any discipline where actual costs are coded but there is zero or minimal budget — this is likely a coding mistake or budget misalignment. However, if less than 1% of total costs are coded to a wrong phase or discipline, do not treat it as a severe issue — just note it. Flag work performed without approved change orders. Call out phases where actual spending does not follow the budget distribution — this suggests poor cost control or phase mismanagement. GOOD PERFORMANCE: If multiple disciplines are tracking on or under budget with healthy CPI/SPI, acknowledge the good work — call out what the team is doing well. If nothing noteworthy, respond with exactly: NO_INSIGHTS';

// ── Billing document assistants ──
// The chat box inside the Pre-Export Checks modal (a summary's backup PDF)
// and inside the Time Card Approval preview (a day's field ticket). Their
// prompts live here beside the insights prompt so one admin screen tunes
// them all; the Apply wire format is NOT here — billingAiChat.js builds that
// into the context turn in code, so editing this text can't break the
// buttons.
export const DEFAULT_BILLING_SUMMARY_PROMPT = 'You help a billing administrator revise a cost-plus invoice backup document for Strike, an industrial construction contractor, in the last minute before it goes to the client. The document is the summary\'s backup PDF: every actual cost transaction on the invoice, grouped Labor / Equipment / Third Party / Pass-Through, each line billed at its cost class markup — except labor and mapped equipment tiers, which bill hours x the job\'s rate sheet, and pass-through freight/delivery, which bills at cost. A fuel surcharge rides on top of the equipment lines only. Answer in short prose and tight bullets. Be specific with dollar amounts and name the line you mean by its date, person or vendor, and cost. HOW THIS DATA BEHAVES, treat as ground truth: the client pays cost plus markup, so a line on the wrong cost class or the wrong labor classification is a real dollar error, not a cosmetic one. A labor line\'s billed classification comes from the rate sheet map; when JDE\'s payroll class is simply wrong for the person, the fix is the per-line classification override, not a markup change. Burden, taxes and insurance cost types (513xx/514xx/516xx/517xx) never bill and must never appear. A line already on an uploaded summary can still be corrected, but say that the summary has to be re-uploaded to FTMS afterward. Freight and delivery bill at cost as pass-through; putting a markup on them is the mistake that gets noticed. A zero-rate labor line would bill $0 — always a fail. WHAT TO DO: when the user asks for a change, make it. When they ask what is wrong, work from the pre-export check findings already in the document and from the lines themselves, and lead with anything that would make the client\'s invoice wrong if it were signed as-is. Do not invent problems to have something to say; a clean document gets a short "nothing I would change" and the one or two things worth a second look. Never guess at a number you were not given. If the fix is not something this screen can make, say plainly what to change and which screen owns it (Rates & Rules for the rate sheet and pass-through rules, the Transactions view for selection, Time Card Approval for shift and ticket corrections).';

export const DEFAULT_FIELD_TICKET_PROMPT = 'You help a billing administrator revise a daily field ticket for Strike, an industrial construction contractor, before it goes to the client representative for signature. The ticket is one work date and one shift: a matrix of people down the side and the job\'s WBS phase codes across the top, with each person\'s billed classification, crew lead, hours per phase code, overtime, and per diem / truck pay / rig pay flags, then the equipment units and their hours. Answer in short prose and tight bullets. Name people by name and hours to the quarter hour. HOW THIS DATA BEHAVES, treat as ground truth: the hours come out of the HeavyJob / HEET feed, which codes nearly every timecard as Shift 1 — so a person appearing on the day sheet who actually worked nights is the single most common error on this document, and the shift correction is the fix. Someone at orientation bills 5 hours and may carry an Orientation classification. Phase code 100105 is folded into each person\'s largest phase code on the client\'s ticket; its presence alone is not an error. Salaried Director and Senior Director staff can post as one anonymous Miscellaneous line under employee id 999999 rather than by name. The billed classification comes from the field\'s Staff List (Tier 4) first and the pay-class map second; a blank or generic classification is worth flagging. More than 16 hours in a day, or the same person on both the day and the night sheet for one date, needs a look. Equipment hours carry no shift, so equipment rides the day sheet. WHAT TO DO: when the user asks for a change, make it. When they ask what is wrong, lead with anything that would make the ticket wrong or unsupported if the client signed it now — a person on the wrong shift, hours that do not add up, a missing or generic classification, a phase code only one person is on. Do not invent problems; a clean ticket gets a short "nothing I would change". Never guess a number you were not given. The only thing this screen can change is which shift a person is on; for anything else say plainly what to fix and where it lives (the hours come from HeavyJob, the classification from the field\'s Staff List workbook, the rates from Rates & Rules).';

// `def` is the section's own default prompt; `legacy` marks the two sections
// that predate per-section storage and still honour the old global
// localStorage key.
export const AI_PROMPT_SECTIONS = [
    { id: 'overview', label: 'Overview', def: DEFAULT_AI_SYSTEM_PROMPT, legacy: true },
    { id: 'summary', label: 'Summary', def: DEFAULT_AI_SYSTEM_PROMPT, legacy: true },
    { id: 'billingSummary', label: 'Invoice Backup', def: DEFAULT_BILLING_SUMMARY_PROMPT },
    { id: 'fieldTicket', label: 'Field Ticket', def: DEFAULT_FIELD_TICKET_PROMPT },
];

/** The built-in prompt for a section — what Reset restores, and the floor
 *  every lookup falls back to. */
export function defaultPromptFor(section) {
    const s = AI_PROMPT_SECTIONS.find((x) => x.id === section);
    return (s && s.def) || DEFAULT_AI_SYSTEM_PROMPT;
}

// ── Server-backed prompt storage (shared across all users) ────────────
// The prompt lives in PCV.Forecast_User_Data under a dedicated tab, keyed by
// a global sentinel BU_ID / Version_GUID (it is not job- or version-specific).
// Forecast_Value is capped (the updateUserDataArray client truncates to 1024),
// so a prompt is split into <=1000-char chunks across rows:
//   rowId = section, fieldName = 'chunks'    → number of chunks (count)
//   rowId = section, fieldName = 'prompt_<i>' → the i-th chunk of text
// A 'chunks' value of 0 means "no override" → callers fall back to the default.
export const AI_PROMPT_TAB = 'AI Summary';
export const AI_PROMPT_BU_ID = 'GLOBAL';
// readUserData binds Version_GUID as sql.Int, so the sentinel must be numeric.
// BU_ID = 'GLOBAL' already isolates these rows from every real job/version.
export const AI_PROMPT_VERSION_GUID = '0';
const AI_PROMPT_CHUNK_SIZE = 1000;

function aiPromptRow(section, fieldName, value) {
    return {
        id: AI_PROMPT_BU_ID,
        version: AI_PROMPT_VERSION_GUID,
        tab: AI_PROMPT_TAB,
        rowId: section,
        fieldName,
        value: value == null ? '' : String(value),
        // These columns are interpolated unconditionally by updateUserDataArray;
        // pass empty strings so they don't land as the literal text 'undefined'.
        costType: '', costCode: '', vendor: '', po: '', equipment: '',
    };
}

/** Fetch the shared per-section prompts from SQL. Returns { [section]: text }
 *  containing only sections that have a non-empty override saved. */
export async function loadServerPrompts() {
    const out = {};
    try {
        const res = await loadUserDataRaw({ id: AI_PROMPT_BU_ID, forecastGuid: AI_PROMPT_VERSION_GUID });
        const rows = (res && res.data) || [];
        // latest-wins per (rowId, fieldName) in case any historical dupes exist
        const latest = {};
        for (const r of rows) {
            if (r.Forecast_Tab !== AI_PROMPT_TAB) continue;
            const k = `${r.Forecast_Row_ID}|${r.Forecast_Field_Name}`;
            const t = new Date(r.DSS_Updated_Datetime || r.DSS_Created_Datetime || r.DSS_Created_Date || 0).getTime();
            if (!latest[k] || t >= latest[k].t) latest[k] = { t, value: r.Forecast_Value };
        }
        for (const { id } of AI_PROMPT_SECTIONS) {
            const countCell = latest[`${id}|chunks`];
            if (!countCell) continue;
            const count = parseInt(countCell.value, 10);
            if (!count || count < 1) continue; // 0 = cleared → fall back to default
            let text = '';
            for (let i = 0; i < count; i++) {
                const cell = latest[`${id}|prompt_${i}`];
                text += (cell && cell.value) || '';
            }
            if (text.trim()) out[id] = text;
        }
    } catch (e) {
        console.warn('[AISummary] loadServerPrompts failed:', e);
    }
    return out;
}

/** Save a section's prompt to SQL (chunked). */
export async function saveServerPrompt(section, text) {
    const str = text == null ? '' : String(text);
    const chunks = [];
    for (let i = 0; i < str.length; i += AI_PROMPT_CHUNK_SIZE) {
        chunks.push(str.slice(i, i + AI_PROMPT_CHUNK_SIZE));
    }
    const rows = [aiPromptRow(section, 'chunks', String(chunks.length))];
    chunks.forEach((c, i) => rows.push(aiPromptRow(section, `prompt_${i}`, c)));
    await updateUserDataArray(rows);
    refreshServerPrompts();
}

/** Clear a section's server override (revert everyone to the default prompt). */
export async function clearServerPrompt(section) {
    await updateUserDataArray([aiPromptRow(section, 'chunks', '0')]);
    refreshServerPrompts();
}

// Memoized so concurrent AISummary mounts share one fetch. Admin calls
// refreshServerPrompts() after a save so the next insight picks up the change.
let _serverPrompts = null;
let _serverPromptsPromise = null;

export function refreshServerPrompts() {
    _serverPrompts = null;
    _serverPromptsPromise = null;
}

async function ensureServerPrompts() {
    if (_serverPrompts) return _serverPrompts;
    if (!_serverPromptsPromise) _serverPromptsPromise = loadServerPrompts();
    try {
        _serverPrompts = await _serverPromptsPromise;
    } catch {
        _serverPrompts = {};
    }
    return _serverPrompts || {};
}

// Resolve the system prompt at fetch time: shared server prompt first, then
// this browser's localStorage cache, then the built-in default.
async function getSystemPromptAsync(section) {
    try {
        const server = await ensureServerPrompts();
        if (section && server[section] && server[section].trim()) return server[section];
    } catch { /* ignore — fall back below */ }
    return getSystemPrompt(section);
}

/** The live system prompt for a section: the shared server prompt, then this
 *  browser's cache, then the built-in default. Public for the billing chat
 *  boxes, which resolve it per send. */
export async function resolveSystemPrompt(section) {
    return getSystemPromptAsync(section);
}

/** Record what was actually sent as the user turn, so the admin AI tab's
 *  read-only box can show it. Best-effort — never fatal. */
export function captureUserPrompt(section, text) {
    if (!section) return;
    try {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(userPromptStorageKey(section), String(text || ''));
        }
    } catch { /* ignore storage errors */ }
}

export function sectionStorageKey(section) {
    return `${AI_PROMPT_STORAGE_KEY}:${section}`;
}

export function userPromptStorageKey(section) {
    return `${AI_USER_PROMPT_STORAGE_KEY}:${section || 'unknown'}`;
}

function deriveSection(pageKey) {
    if (typeof pageKey !== 'string') return null;
    const prefix = pageKey.split('_')[0];
    return AI_PROMPT_SECTIONS.some(s => s.id === prefix) ? prefix : null;
}

function getSystemPrompt(section) {
    try {
        if (typeof localStorage !== 'undefined') {
            if (section) {
                const perSection = localStorage.getItem(sectionStorageKey(section));
                if (perSection && perSection.trim()) return perSection;
            }
            // The old global key held the insights prompt. Only the sections
            // that predate per-section storage may inherit it — handing it to
            // the billing assistants would swap their prompt for an entirely
            // different job's.
            const spec = AI_PROMPT_SECTIONS.find((x) => x.id === section);
            if (!section || (spec && spec.legacy)) {
                const legacy = localStorage.getItem(AI_PROMPT_STORAGE_KEY);
                if (legacy && legacy.trim()) return legacy;
            }
        }
    } catch { /* ignore storage errors */ }
    return defaultPromptFor(section);
}

// ── Module-level cache (survives component unmount / remount) ────────
const _results = {};      // pageKey → { summary, error, done }
const _fetched = {};      // pageKey → true once fetch has begun
const _subscribers = {};  // pageKey → Set<() => void>

function subscribe(key, fn) {
    if (!_subscribers[key]) _subscribers[key] = new Set();
    _subscribers[key].add(fn);
    return () => _subscribers[key].delete(fn);
}

function notify(key) { _subscribers[key]?.forEach(fn => fn()); }

async function runFetch(key, prompt, section) {
    _results[key] = { summary: '', error: '', done: false };
    notify(key);
    try {
        if (section && typeof localStorage !== 'undefined') {
            try { localStorage.setItem(userPromptStorageKey(section), prompt || ''); } catch { /* ignore */ }
        }
        console.log('[AISummary] Fetching:', API_BASE + 'api/openaiChat');
        const res = await fetch(API_BASE + 'api/openaiChat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                messages: [
                    { role: 'system', content: await getSystemPromptAsync(section) },
                    { role: 'user', content: prompt },
                ],
                max_tokens: 1024,
                temperature: 0.4,
            }),
        });
        console.log('[AISummary] Response status:', res.status, res.statusText);
        console.log('[AISummary] Response headers:', Object.fromEntries(res.headers.entries()));

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                const trimmedLine = line.trim();
                if (!trimmedLine.startsWith('data: ')) continue;
                const payload = trimmedLine.slice(6);
                console.log('[AISummary] SSE chunk:', payload);
                if (payload === '[DONE]') break;
                try {
                    const parsed = JSON.parse(payload);
                    console.log('[AISummary] Parsed:', parsed);
                    if (parsed.content) {
                        accumulated += parsed.content;
                        _results[key] = { summary: accumulated, error: '', done: false };
                        notify(key);
                    }
                } catch { /* skip malformed chunks */ }
            }
        }

        const final = (!accumulated || accumulated.trim() === 'NO_INSIGHTS') ? '' : accumulated;
        _results[key] = { summary: final, error: '', done: true };
    } catch (err) {
        _results[key] = { summary: '', error: 'Failed to generate insights: ' + err.message, done: true };
    }
    notify(key);
}

// ── Styles ───────────────────────────────────────────────────────────
const styles = {
    container: {
        position: 'relative',
    },
    titleRow: {
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
    },
    titleIcon: {
        display: 'flex', alignItems: 'center',
    },
    title: {
        margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '-0.01em', textTransform: 'uppercase',
    },
    content: (palette) => ({
        padding: 12, borderRadius: 8, border: `1px solid ${palette.border}`,
        background: palette.blue50, fontSize: 12, lineHeight: 1.7,
        color: palette.text, minHeight: 40,
    }),
    error: (palette) => ({
        padding: 10, borderRadius: 8, fontSize: 12,
        background: palette.redLight || '#fee2e2', color: palette.red || '#dc2626',
        border: `1px solid ${palette.red || '#dc2626'}33`,
    }),
    loading: {
        display: 'flex', alignItems: 'center', gap: 8, padding: 12, fontSize: 12, color: '#64748b',
    },
    dot: (delay) => ({
        width: 6, height: 6, borderRadius: '50%', background: '#3b82f6',
        animation: `aiDotPulse 1.2s ease-in-out ${delay}s infinite`,
    }),
};

const sparkleIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" />
    </svg>
);

/** Lightweight markdown → React elements (bold, bullets, line breaks) */
function renderMarkdown(text) {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let key = 0;
    for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed) { elements.push(<br key={key++} />); continue; }
        // bullet line
        const bulletMatch = trimmed.match(/^[-•*]\s+(.*)/);
        const content = bulletMatch ? bulletMatch[1] : trimmed;
        // inline bold **text**
        const parts = content.split(/(\*\*[^*]+\*\*)/);
        const rendered = parts.map((p, i) => {
            if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
            return p;
        });
        if (bulletMatch) {
            elements.push(<div key={key++} style={{ display: 'flex', gap: 6, marginBottom: 2 }}><span style={{ flexShrink: 0 }}>•</span><span>{rendered}</span></div>);
        } else {
            elements.push(<div key={key++}>{rendered}</div>);
        }
    }
    return elements;
}

// ── Component ────────────────────────────────────────────────────────
export default function AISummary({ buildPrompt, palette, pageKey, section }) {
    const [, rerender] = useState(0);
    const buildPromptRef = useRef(buildPrompt);
    buildPromptRef.current = buildPrompt;
    const resolvedSection = section || deriveSection(pageKey);

    // Subscribe to module-level cache updates so streaming chunks trigger re-renders
    useEffect(() => subscribe(pageKey, () => rerender(n => n + 1)), [pageKey]);

    // Poll for data readiness, fire fetch exactly once per pageKey
    useEffect(() => {
        // Clear any stale cache for this pageKey so a fresh fetch always runs
        delete _fetched[pageKey];
        delete _results[pageKey];

        let cancelled = false;
        let pollTimer;

        const tryGenerate = () => {
            if (cancelled || _fetched[pageKey]) return;
            const prompt = buildPromptRef.current();
            if (!prompt) {
                pollTimer = setTimeout(tryGenerate, 500);
                return;
            }
            _fetched[pageKey] = true;
            console.log('[AISummary] Prompt for', pageKey, ':\n', prompt);
            runFetch(pageKey, prompt, resolvedSection);
        };

        tryGenerate();

        return () => {
            cancelled = true;
            if (pollTimer) clearTimeout(pollTimer);
            // Do NOT abort – let the fetch complete in the background
        };
    }, [pageKey]);

    const result = _results[pageKey] || {};
    const loading = _fetched[pageKey] && !result.done;

    return (
        <div style={styles.container}>
            <style>{`
                @keyframes aiDotPulse {
                    0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
                    40% { opacity: 1; transform: scale(1.2); }
                }
            `}</style>

            <div style={styles.titleRow}>
                <span style={styles.titleIcon}>{sparkleIcon}</span>
                <h3 style={{ ...styles.title, color: palette.text }}>AI Insights</h3>
            </div>

            {loading && !result.summary && (
                <div style={styles.loading}>
                    <div style={styles.dot(0)} />
                    <div style={styles.dot(0.2)} />
                    <div style={styles.dot(0.4)} />
                    <span>Analyzing data...</span>
                </div>
            )}

            {result.error && <div style={styles.error(palette)}>{result.error}</div>}

            {result.summary && (
                <div style={styles.content(palette)}>
                    {renderMarkdown(result.summary)}
                </div>
            )}
        </div>
    );
}
